import { type FC, type PropsWithChildren } from 'react';
import { Controller, useFieldArray, type Control } from 'react-hook-form';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { type EditAuditFormBackgroundData } from '#apps/client-web/pages/audit-form-background/types';
import { getFormErrorAttributes } from '#apps/client-web/app/helpers/react-hook-form';
import { FileInput } from '#apps/client-web/features/file-storage/components/file-input/file-input';
import { type FormAttachment } from '#apps/client-web/pages/audit-form-mechanical-entry/types';

import {
  SubFormDialog,
  type SubFormDialogProps,
} from '#packages/web/layouts/dialog/templates/sub-form';
import { Fab } from '#packages/web/foundations/buttons/fab';
import { DeleteConfirmationDialog } from '#packages/web/layouts/dialog/templates/delete-confirmation';
import { useDialog } from '#packages/web/hooks/use-dialog';
import { MessagePage } from '#packages/web/layouts/page/templates/message-page';
import { Message } from '#packages/web/foundations/feedback/message';

export type RoofingFormDialogProps = Pick<
  SubFormDialogProps,
  'onClose' | 'open'
> & {
  control: Control<EditAuditFormBackgroundData>;
};

export const RoofingFormDialog: FC<
  PropsWithChildren<RoofingFormDialogProps>
> = ({ open, control, onClose }) => {
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'envelope.roofingTypes',
  });

  const deleteEntryDialog = useDialog<number>();
  const deleteAttachmentDialog = useDialog<{ proceed: () => void }>();

  const handleAttachmentsChange = (
    entryIndex: number,
    newAttachments: FormAttachment[],
  ) => {
    const prevAttachments = fields[entryIndex]?.attachments ?? [];
    const newlyStarred = newAttachments.find(
      (a) =>
        a.isSelectedForReport &&
        !(prevAttachments.find((p) => p.name === a.name)?.isSelectedForReport ?? false),
    );
    if (newlyStarred) {
      replace(
        fields.map(({ id: _id, ...entry }, j) => ({
          ...entry,
          attachments:
            j === entryIndex
              ? newAttachments
              : (entry.attachments ?? []).map((a) => ({
                  ...a,
                  isSelectedForReport: false,
                })),
        })),
      );
    } else {
      const { id: _id, ...entryData } = fields[entryIndex];
      replace(
        fields.map(({ id: __id, ...e }, j) =>
          j === entryIndex ? { ...entryData, attachments: newAttachments } : e,
        ),
      );
    }
  };

  return (
    <SubFormDialog title="Roofing types" open={open} onClose={onClose}>
      <Stack spacing={3} sx={{ pb: 10 }}>
        {fields.length === 0 && (
          <MessagePage
            message={<Message>No roofing types added yet. Tap + to add one.</Message>}
          />
        )}
        {fields.map((field, index) => (
          <Box key={field.id}>
            <Stack spacing={2}>
              <Grid container alignItems="center" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">
                  Roofing type {index + 1}
                </Typography>
                <IconButton
                  size="small"
                  color="error"
                  aria-label="remove roofing type"
                  onClick={() => deleteEntryDialog.open(index)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Controller
                    control={control}
                    name={`envelope.roofingTypes.${index}.types`}
                    rules={{ minLength: 2, maxLength: 120 }}
                    render={({ field: f, fieldState }) => (
                      <FormControl fullWidth {...getFormErrorAttributes(fieldState.error)}>
                        <TextField
                          {...f}
                          label="Types"
                          type="text"
                          multiline
                          rows={5}
                          variant="outlined"
                          {...getFormErrorAttributes(fieldState.error)}
                        />
                        <FormHelperText>{fieldState.error?.message}</FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Controller
                    control={control}
                    name={`envelope.roofingTypes.${index}.attachments`}
                    render={({ field: f, fieldState }) => (
                      <FormControl fullWidth {...getFormErrorAttributes(fieldState.error)}>
                        <FileInput
                          label="Attachments"
                          value={(f.value ?? []) as FormAttachment[]}
                          onChange={(newAttachments) =>
                            handleAttachmentsChange(index, newAttachments as FormAttachment[])
                          }
                          maxSelectedForReport={1}
                          onRemoveRequest={(_, proceed) =>
                            deleteAttachmentDialog.open({ proceed })
                          }
                          {...getFormErrorAttributes(fieldState.error)}
                        />
                        <FormHelperText>{fieldState.error?.message}</FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </Stack>
            {index < fields.length - 1 && <Divider sx={{ mt: 3 }} />}
          </Box>
        ))}
      </Stack>

      <Fab tooltip="Add roofing type" onClick={() => append({})} />

      <DeleteConfirmationDialog
        open={deleteEntryDialog.isOpen}
        dialogTitle="Remove Roofing Type"
        confirmButtonLabel="Remove"
        onConfirm={() => {
          remove(deleteEntryDialog.data!);
          deleteEntryDialog.close();
        }}
        onCancel={() => deleteEntryDialog.close()}
      />

      <DeleteConfirmationDialog
        open={deleteAttachmentDialog.isOpen}
        dialogTitle="Remove Attachment"
        confirmButtonLabel="Remove"
        onConfirm={() => {
          deleteAttachmentDialog.data?.proceed();
          deleteAttachmentDialog.close();
        }}
        onCancel={() => deleteAttachmentDialog.close()}
      />
    </SubFormDialog>
  );
};
